import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Banknote, Loader2 } from "lucide-react";

const TEAL = "#2f6f6b";

export default function PayrollSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.payrollSettings().then((d) => setSettings(d.settings))
      .catch(() => toast.error("Could not load payroll settings."));
  }, []);

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const saveSettings = async () => {
    setSaving(true);
    try { await api.updatePayrollSettings(settings); toast.success("Payroll settings saved."); }
    catch { toast.error("Save failed."); } finally { setSaving(false); }
  };

  if (!settings) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const Cat = ({ k, label, hint }) => (
    <div>
      <Label className="text-[11px] font-semibold text-slate-600">{label}</Label>
      <Input value={settings[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder="Work Type External ID" className="h-9 text-sm mt-1" data-testid={`payroll-${k}`} />
      {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-2.5">
        <Banknote className="h-5 w-5" style={{ color: TEAL }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">Payroll Export Settings</h1>
      </div>
      <p className="text-[12px] text-slate-500 -mt-3">Integration configuration only. Pay runs and employee ID mappings live in <b>Accounts → Payroll</b>.</p>

      {/* Engine + rules */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-800">Payroll engine</div>
            <p className="text-[11px] text-slate-500">These rules apply to the selected engine only.</p>
          </div>
          <select value={settings.engine} onChange={(e) => set("engine", e.target.value)} className="h-9 text-sm rounded-md border border-slate-200 px-2" data-testid="payroll-engine">
            <option value="employment_hero">Employment Hero Payroll</option>
          </select>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
          <div><div className="text-sm font-medium text-slate-700">Enable payroll export</div>
            <p className="text-[11px] text-slate-500">Exports approved pay times. Award penalties, overtime and minimum engagement are applied by Employment Hero / Pay Cat.</p></div>
          <Switch checked={!!settings.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="payroll-enabled" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-lg border border-slate-100 p-3 space-y-2">
            <div className="flex items-center justify-between"><span className="text-xs font-semibold text-slate-700">Sleepovers</span>
              <Switch checked={!!settings.sleepover_enabled} onCheckedChange={(v) => set("sleepover_enabled", v)} data-testid="payroll-sleepover-enabled" /></div>
            <Cat k="sleepover_category" label="Sleepover Work Type External ID" hint="Default 1983609 · exported as Work Type (W) rows with the sleep period's start and end; Pay Cat's ruleset applies the allowance" />
            <Cat k="overnight_active_category" label="Active work during sleepover Work Type External ID" hint="Default 7676149 · one Work Type (W) row per approved awake period, rounded to 15 minutes; the sleepover is split around it" />
          </div>
          {/* KM / Vehicle — single category */}
          <div className="rounded-lg border border-slate-100 p-3 space-y-2">
            <div className="flex items-center justify-between"><span className="text-xs font-semibold text-slate-700">KM / Vehicle allowance</span>
              <Switch checked={!!settings.km_enabled} onCheckedChange={(v) => set("km_enabled", v)} data-testid="payroll-km-enabled" /></div>
            <Cat
              k="km_ato_category"
              label="KM allowance Work Type External ID"
              hint="Default 7676643 · one Work Type (W) row per shift · units = claimed km, over the shift's pay times"
            />
            <Cat k="worked_category" label="Worked hours Pay Category External ID (leave blank)" hint="Blank = Pay Cat interprets ordinary, penalty and overtime from the times. Normal shifts import correctly with this blank." />
          </div>
        </div>

        {/* Shift description format */}
        <div className="rounded-lg border border-slate-100 p-3 space-y-2">
          <div>
            <span className="text-xs font-semibold text-slate-700">Shift description format</span>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Customise the Comments field on each timesheet row. Available tokens:{" "}
              <code className="bg-slate-100 px-1 rounded text-[10px]">{"{first_name}"}</code>{" "}
              <code className="bg-slate-100 px-1 rounded text-[10px]">{"{last_name}"}</code>{" "}
              <code className="bg-slate-100 px-1 rounded text-[10px]">{"{full_name}"}</code>{" "}
              <code className="bg-slate-100 px-1 rounded text-[10px]">{"{shift_id}"}</code>{" "}
              <code className="bg-slate-100 px-1 rounded text-[10px]">{"{start_time}"}</code>{" "}
              <code className="bg-slate-100 px-1 rounded text-[10px]">{"{end_time}"}</code>
            </p>
          </div>
          <div>
            <Label className="text-[11px] font-semibold text-slate-600">Template</Label>
            <Input
              value={settings.shift_description_template || ""}
              onChange={(e) => set("shift_description_template", e.target.value)}
              placeholder="{first_name} · {shift_id} · {start_time}–{end_time}"
              className="h-9 text-sm mt-1 font-mono"
              data-testid="payroll-shift_description_template"
            />
            <p className="text-[10px] text-slate-400 mt-0.5">
              Preview (example):{" "}
              <span className="font-medium text-slate-600">
                {(settings.shift_description_template || "{first_name} · {shift_id} · {start_time}–{end_time}")
                  .replace("{first_name}", "Emma")
                  .replace("{last_name}", "Smith")
                  .replace("{full_name}", "Emma Smith")
                  .replace("{shift_id}", "SHIFT-00123")
                  .replace("{start_time}", "09:00")
                  .replace("{end_time}", "15:00")}
              </span>
            </p>
          </div>
        </div>

        <div className="flex justify-end"><Button onClick={saveSettings} disabled={saving} style={{ backgroundColor: TEAL }} data-testid="payroll-save-settings">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}</Button></div>
      </div>
    </div>
  );
}
